/**
 * The in-between drawings of the high-five, built point by point from the final pose so every pose keeps the
 * same path commands and SMIL can morph between them. A pose curls the fingers, tucks the thumb and turns the
 * palm about the forearm with a true perspective divide, so the far edge shrinks and the near edge does not.
 * Nothing bends at the wrist: the arm leans back rigidly, so the line into the sleeve keeps its shape.
 */

export type Pose = {
	/** How far, in artboard units, the fingertips curl forward. */
	curl: number;
	/** Degrees the palm is turned away from the other hand. */
	turn: number;
	/** Degrees the thumb folds in towards the fingers, hinged at its base; negative spreads it out. */
	thumb: number;
};

type Box = { left: number; right: number; top: number; bottom: number };

export type HandRig = {
	/** 1 for the left hand, -1 for the right: which way is towards the clap. */
	side: 1 | -1;
	/** Above this line the outline belongs to the fingers. */
	knuckles: number;
	/** The vertical line the palm turns about, and the height it turns at. */
	axis: readonly [number, number];
	/** Absent where the thumb is out of frame. */
	thumb?: { base: readonly [number, number]; box: Box };
};

const FOCAL = 420;
/** Artboard units over which the thumb goes from moving rigidly to staying with the palm. */
const THUMB_BLEND = 5;

function ramp(inside: number) {
	const t = Math.min(1, Math.max(0, inside / THUMB_BLEND));
	return t * t * (3 - 2 * t);
}

function inBox(x: number, y: number, box: Box) {
	return ramp(x - box.left) * ramp(box.right - x) * ramp(y - box.top) * ramp(box.bottom - y);
}

function moved(x: number, y: number, rig: HandRig, pose: Pose, hand: boolean): [number, number] {
	let px = x;
	let py = y;

	if (hand && rig.thumb) {
		const w = inBox(x, y, rig.thumb.box);
		const angle = ((-rig.side * pose.thumb * w) / 180) * Math.PI;
		const [bx, by] = rig.thumb.base;
		const rx = px - bx;
		const ry = py - by;
		px = bx + rx * Math.cos(angle) - ry * Math.sin(angle);
		py = by + rx * Math.sin(angle) + ry * Math.cos(angle);
	}

	if (hand) {
		const fingers = Math.min(1, Math.max(0, (rig.knuckles - y) / 60));
		px += rig.side * pose.curl * 0.5 * fingers;
		py += pose.curl * fingers;
	}

	const [ax, ay] = rig.axis;
	const turn = (pose.turn / 180) * Math.PI;
	const across = px - ax;
	const scale = FOCAL / (FOCAL + rig.side * across * Math.sin(turn));
	return [ax + across * Math.cos(turn) * scale, ay + (py - ay) * scale];
}

/** `hand` is false for the cuff: it turns with the forearm, but has no fingers or thumb to move. */
export function posed(d: string, rig: HandRig, pose: Pose, hand = true) {
	const numbers = d.match(/-?\d*\.?\d+(?:e-?\d+)?/g) ?? [];
	const out: string[] = [];
	for (let i = 0; i + 1 < numbers.length; i += 2) {
		const [x, y] = moved(Number(numbers[i]), Number(numbers[i + 1]), rig, pose, hand);
		out.push(x.toFixed(2), y.toFixed(2));
	}
	let next = 0;
	return d.replace(/-?\d*\.?\d+(?:e-?\d+)?/g, () => out[next++] ?? '0');
}
